import type { User } from 'firebase/auth';
import type { ComponentProps } from 'react';
import * as Sentry from '@sentry/nextjs';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import React from 'react';

import { type RouterOutputs } from '@poseidon/server/api';

import { api } from '@/utils/api';
import isClientSide from './isClientSide';

export type PoseidonApiAuthRes = NonNullable<RouterOutputs['user']['me']>;

export type AuthContextProps = [
  | (PoseidonApiAuthRes & {
      isPending: boolean;
      isError: boolean;
    })
  | {
      isPending: boolean;
      isError: boolean;
      user: null;
    },
];

const defaultValue: AuthContextProps = [
  {
    user: null,
    isPending: true,
    isError: false,
  },
];

const AuthContext = React.createContext<AuthContextProps>(defaultValue);

export const AuthProvider = ({ children }: ComponentProps<'div'>) => {
  const [firebaseUser, setFirebaseUser] = React.useState<User | null>();

  const { data, isPending, isError } = api.user.me.useQuery(undefined, {
    enabled: Boolean(firebaseUser),
  });

  React.useEffect(() => {
    if (isClientSide()) {
      const unsubscribe = onAuthStateChanged(getAuth(), (u) => {
        if (u == null) {
          Sentry.setUser(null);
          Sentry.setTag('user_email', undefined);
        } else {
          const sUser = { id: u.uid, email: u.email ?? undefined };
          Sentry.setUser(sUser);
          Sentry.setTag('user_email', u.email ?? undefined);
        }

        setFirebaseUser(u);
      });
      return () => unsubscribe();
    }
  }, [setFirebaseUser]);

  const authData = data == null ? { user: null } : data;
  const value: AuthContextProps = [
    {
      isPending: firebaseUser === null ? false : isPending,
      isError,
      ...authData,
    },
  ];

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => React.useContext(AuthContext);
